/**
 * **초당 피해** — 샌드백에서 잰 값의 표 (UNI-Q161).
 *
 * > 사용자: *"전체 상위 10 + 직업별 상위 10, 그리고 내 순위를 아래에 적는다.
 * >  「이번 세계」와 「역대」 두 갈래를 탭이나 토글로 나눈다."*
 *
 * ⚠ **숫자는 전부 서버가 준다.** 여기서 정렬을 다시 하거나 순위를 다시 세지 않는다 —
 * 받은 20명을 화면에서 재정렬하면 **그 20명 안에서의 순위**가 되어 표 밖의 사람이
 * 사라진다(랭킹 패널이 같은 함정을 주석으로 적어둔 그 자리다).
 *
 * 표 모양은 `rank.js` 와 **같은 결**이다 — 메달 색·`.item`·`.nm`·금색 꼬리.
 * 새 디자인 언어를 만들지 않는다.
 */
import { S, esc } from '../../core/state.js';
import { loadingOr, muted, num, sec } from './common.js';
// 레벨 글자는 단일 출처다 — 서버가 운영자 레벨을 '???' 로 보내면 직접 적은 자리가 NaN 이 된다
import { levelLabel } from '../../core/level-label.js';
import { starMark } from '../star.js';

/** 직업 이름 — 서버가 보낸 값 그대로를 사람 말로 바꾼다 */
function klassKo(k) { return k === 'ranged' ? '원거리' : '근접'; }

/** 순위 한 줄. `me` 면 강조한다 */
function row(r, i, me) {
  const medal = i === 0 ? '#d4a83c' : i === 1 ? '#b9bcc2' : i === 2 ? '#b07d4a' : 'inherit';
  return `<div class="item ${me ? 'sel' : ''}">
    <span class="qt" style="width:26px;text-align:center;color:${medal}">${i + 1}</span>
    <span class="nm">${starMark(r.name)}${esc(String(r.name ?? ''))} <span class="qt">${klassKo(r.klass)} · Lv.${levelLabel(r.level)}</span></span>
    <span class="qt" style="color:var(--gold);font-size:var(--ui-body)">${num(Number(r.dps) || 0)}</span></div>`;
}

/** 표본이 모자라면 **있는 만큼만** 그린다. 빈 줄을 지어내지 않는다 */
function list(rows, myName) {
  if (!rows || !rows.length) return muted('아직 잰 사람이 없다.');
  return rows.map((r, i) => row(r, i, myName && r.name === myName)).join('');
}

export function render() {
  const world = S.dpsScope !== 'all';
  let h = sec('초당 피해 <em>샌드백</em>',
    '<div class="row">'
    + `<button class="${world ? 'sel' : 'ghost'}" onclick="act.dpsScope('world')">이번 세계</button>`
    + `<button class="${world ? 'ghost' : 'sel'}" onclick="act.dpsScope('all')">역대</button>`
    // 명예의 전당(`hall.js`)과 같은 결 — 랭킹에서 건너왔으니 돌아갈 문도 같은 자리에 둔다
    + '<button class="ghost" onclick="act.dpsBack()">랭킹으로</button>'
    + '</div>'
    + muted(world
      ? '<b>이번 세계</b>의 기록이다 — 세계가 초기화되면 몸과 함께 사라진다. '
        + '네 마을 광장의 <b>샌드백</b>을 누르면 첫 타격부터 재기 시작한다.'
      : '<b>역대</b> 기록이다 — 계정에 남아 세계가 바뀌어도 사라지지 않는다. '
        + '직업을 바꾸면 <b>직업별로 따로</b> 쌓인다(근접 기록과 원거리 기록은 견줄 값이 아니다).'));

  const b = S.dpsBoard;
  if (!b || (b.scope === 'all') === world) {
    // R257 — 이 표는 소켓(사냥터에서는 조회 다리)으로 온다. `S.loadErr` 는 창고·랭킹 HTTP 몫이라 여기서 읽으면 남의 실패가 뜬다
    return h + loadingOr('dpsBoard', '불러오는 중…');
  }

  const me = S.you ? S.you.name : '';
  const secsKo = Number(b.secs) || 0;
  h += sec(`전체 상위 ${Number(b.top) || 0}`,
    muted(`<b>${secsKo}초</b> 동안 서버가 센 누적 피해 ÷ ${secsKo}. 표본 ${num(Number(b.mine?.total) || 0)}명.`)
    + list(b.overall, me));

  for (const g of (b.byKlass || [])) {
    h += sec(`${klassKo(g.klass)} 상위 ${Number(b.top) || 0}`, list(g.rows, me));
  }

  /**
   * **내 순위는 맨 아래에 적는다**(사용자 지시). 표 안에 있든 없든 여기 한 줄이 있으면
   * *"나는 몇 등인가"* 를 표를 살펴서 찾지 않아도 된다.
   */
  /**
   * **방금 잰 값**은 최고 기록과 다르다 — 표는 최고만 올리므로, 갱신 못 한 측정은
   * 표 어디에도 안 뜬다. 그러면 사람은 *"방금 잰 게 사라졌다"* 로 읽는다.
   * 서버가 창을 닫으며 보낸 결과(`dpsDone`)를 그대로 적는다.
   */
  const last = S.dpsLast;
  if (last) {
    h += sec('방금 잰 값',
      `<div class="kv"><span>${esc(String(last.bag ?? '샌드백'))}</span><b>${num(Number(last.dps) || 0)} / 초</b></div>`
      + `<div class="kv"><span>누적 · 타수</span><b>${num(Math.round(Number(last.total) || 0))} · ${Number(last.hits) || 0}타</b></div>`
      + muted(`${Number(last.secs) || 0}초 동안 서버가 센 값이다. 최고 기록을 못 넘었으면 표에는 안 오른다.`));
  }

  const mine = b.mine || {};
  const myDps = Number(mine.dps) || 0;
  h += sec('내 순위', myDps > 0
    ? `<div class="ux-facts"><span class="ux-fact">내 초당 피해 <b class="ux-number">${num(myDps)}</b></span>`
      + `<span class="ux-fact">전체 <b><span class="ux-number">${Number(mine.rank) || 0}</span><small>위 / ${num(Number(mine.total) || 0)}명</small></b></span>`
      + `<span class="ux-fact">${klassKo(mine.klass)} <b><span class="ux-number">${Number(mine.klassRank) || 0}</span><small>위</small></b></span></div>`
    : muted('아직 잰 적이 없다. 마을 광장의 샌드백을 눌러 재 보라.'));
  return h;
}
